import { nanoid } from 'nanoid';
import React, { useContext, useRef } from 'react';
import {
  AccessibilityContext,
  ImpersonationContext,
  deepClone,
  fsDocRef,
  fsWriteBatch,
  isStringEmpty,
  useOnOutsideClick,
} from '@monash/portal-frontend-common';
import { Icon, useResponsiveValue } from '@monash/portal-react';
import DraggableWindow from '@/components/ui/draggable-window/DraggableWindow';
import { PageContext } from '@/components/providers/page-provider/PageProvider';
import AdditionalOptions from './additional-options/AdditionalOptions';
import { DataContext } from '@/components/providers/data-provider/DataProvider';
import { useSnackbar } from '@/components/providers/SnackbarProvider';
import EditMenuMobileModal from './EditMenuMobileModal';
import {
  ID_SR_PAGE_HEADING,
  MOBILE_RESPONSIVE,
} from '@/components/ui/main/Main';
import s from './edit-menu-shared.module.scss';
import { getTriggerRefPosition } from '../utils';

const MENU_WIDTH = 280;

const EditMenu = ({
  isShown,
  widgetName,
  setIsShown,
  widgetHeaderRef,
  escapeFocusRef,
  additionalOptions,
  additionalActions,
  data,
  updateData,
  widget,
  setWidgetOrder,
  widgetIndex,
  pageId,
  setIsDeleteWidgetModalShown,
  setIsMoveWidgetModalShown,
  renderWidgetCard,
  confirmDeleteWidgetOnMobile,
  hasAdditionalOptionsInEditMenu,
}) => {
  const desktopContainerRef = useRef();
  const { currentUser } = useContext(ImpersonationContext);

  const { setPortalPreferences } = useContext(DataContext);
  const { pagesData } = useContext(PageContext);
  const { resetAppLiveMsgs } = useContext(AccessibilityContext);
  const { addSnackbar } = useSnackbar();
  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);
  const isMobile = responsiveSize === 'S';
  const isShownForMobile = isShown && isMobile;
  const isShownForDesktop = isShown && !isMobile;

  const handleUpdateSuccess = (newPages) => {
    setWidgetOrder(newPages.customPages[pageId].widgetOrder);
    setPortalPreferences((f) => {
      resetAppLiveMsgs();
      addSnackbar({
        message: `${widgetName} widget has been duplicated.`,
        type: 'success',
      });
      return { ...f, pages: newPages };
    });
  };

  const handleUpdateError = (error, consoleErrorMsg, snackbarErrorMsg) => {
    resetAppLiveMsgs();
    addSnackbar({
      message: snackbarErrorMsg,
      type: 'error',
    });
    console.warn(consoleErrorMsg, error);
  };

  // duplicate
  const duplicateWidget = () => {
    const batch = fsWriteBatch();
    const newWidgetId = nanoid();
    const newWidget = {
      size: widget.size,
      typeId: widget.typeId,
    };

    // duplicate data
    if (data) {
      const widgetDoc = fsDocRef(
        `users/${currentUser.uid}/widgets/${newWidgetId}`
      );
      batch.set(widgetDoc, data);
    }

    const newWidgetOrder = pagesData.customPages[pageId].widgetOrder.toSpliced(
      widgetIndex + 1,
      0,
      newWidgetId
    );

    const preferencesDoc = fsDocRef(`users/${currentUser.uid}`);
    batch.update(preferencesDoc, {
      [`preferences.pages.widgets.${newWidgetId}`]: newWidget,
      [`preferences.pages.customPages.${pageId}.widgetOrder`]: newWidgetOrder,
    });

    batch
      .commit()
      .then(() => {
        const newPages = deepClone(pagesData);
        newPages.widgets = { ...newPages.widgets, [newWidgetId]: newWidget };
        newPages.customPages[pageId].widgetOrder = newWidgetOrder;
        handleUpdateSuccess(newPages);
      })
      .catch((err) =>
        handleUpdateError(
          err,
          '[updatePortalPreferences]: api call error, failed to duplicate widget',
          "We're not able to duplicate the widget right now – please try again later"
        )
      )
      .finally(() => {
        document.querySelector(`#${ID_SR_PAGE_HEADING}`)?.focus();
      });
  };

  useOnOutsideClick({
    refs: [desktopContainerRef],
    fn: () => {
      setIsShown(false);
    },
  });

  const handleKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      setIsShown(false);
      escapeFocusRef.current?.focus();
    }
  };

  const headingText = (
    <>
      <span className={s.headingText}>{widgetName}</span>
      <span>&nbsp;options</span>
    </>
  );

  const deleteWidget = isMobile
    ? confirmDeleteWidgetOnMobile
    : () => {
        setIsDeleteWidgetModalShown(true);
      };

  const moveWidget = () => setIsMoveWidgetModalShown(true);

  const defaultActions = [
    {
      icon: <Icon.Copy size={20} />,
      text: 'Duplicate widget',
      onClick: duplicateWidget,
      datTrackingEvent: 'custom-widget-duplicate',
    },
    {
      icon: <Icon.Move size={20} />,
      text: 'Move widget',
      haspopup: 'dialog',
      onClick: moveWidget,
    },
    {
      icon: <Icon.Trash size={20} />,
      text: 'Delete widget',
      haspopup: 'dialog',
      attention: true,
      onClick: deleteWidget,
      notDismissMenuOnClick: isMobile,
      datTrackingEvent: 'custom-widget-delete',
    },
  ];

  const combinedActions = additionalActions
    ? [...additionalActions, ...defaultActions]
    : defaultActions;

  const renderAdditionalOptions = () =>
    hasAdditionalOptionsInEditMenu ? (
      <AdditionalOptions
        data={data}
        updateData={updateData}
        additionalOptions={additionalOptions}
      />
    ) : null;

  const renderCombinedOptions = () =>
    combinedActions
      ? combinedActions.map((item) => {
          const itemHasPopup = !isStringEmpty(item.haspopup);
          return (
            <button
              key={item.text}
              type="button"
              className={s.menuListItem}
              onClick={(e) => {
                item.onClick(e);
                !item.notDismissMenuOnClick && setIsShown(false);
              }}
              style={{
                color: item.attention
                  ? 'var(--color-intent-attention)'
                  : 'var(--card-text-color)',
              }}
              role="menuitem"
              aria-haspopup={itemHasPopup ? item.haspopup : null}
              data-tracking-event={
                'custom-widget-option-' +
                `${item.text?.toLowerCase().replace(/ /g, '-')}}`
              }
            >
              {item.icon}
              {item.text}
            </button>
          );
        })
      : null;

  return (
    <div className={s.editMenuContainer}>
      <EditMenuMobileModal
        isShown={isShownForMobile}
        renderWidgetCard={renderWidgetCard}
        setIsShown={setIsShown}
        widgetName={widgetName}
        escapeFocusRef={escapeFocusRef}
        renderAdditionalOptions={renderAdditionalOptions}
        renderCombinedOptions={renderCombinedOptions}
      />
      {isShownForDesktop && (
        <DraggableWindow
          ref={desktopContainerRef}
          title={headingText}
          isShowing={isShown}
          focusTrapEnabled={true}
          keyboardDismissalEnabled={true}
          onClose={() => {
            setIsShown(false);
            escapeFocusRef.current?.focus();
          }}
          initialPosition={getTriggerRefPosition({
            ref: widgetHeaderRef,
            menuWidth: MENU_WIDTH,
          })}
          extraRootProps={{
            role: 'dialog',
            'aria-label': `${widgetName} options`,
          }}
          extraStyles={{ maxWidth: MENU_WIDTH }}
        >
          <div className={s.editMenu} onKeyDown={handleKeyDown}>
            <div className={s.editItems}>
              {renderAdditionalOptions()}
              {renderCombinedOptions()}
            </div>
          </div>
        </DraggableWindow>
      )}
    </div>
  );
};

export default EditMenu;
